import { ExternalLink, Heading, Image, TARGET_BLANK, Text, toolkitCx } from "@sundae/ui-toolkit";
import { IImage } from "@sundae/ui-toolkit/dist/types/components/atoms/Image/Image";
import cx from "classnames";
import { FC } from "react";
import Link from "../Link";

interface II18N {
  title: string;
  copy: string;
  cta?: string;
}

export interface IInformationTeaser {
  className?: string;
  image?: IImage;
  i18n: II18N;
  isExternal?: boolean;
  route?: string;
}
/**
 * The InformationTeaser component displays an image, title, and description text
 * with an optional call-to-action button. It is typically used for promoting a specific
 * piece of content or for providing additional information to the user.
 *
 * @component
 * @param {TInformationTeaser} props - The props for the InformationTeaser component.
 * @param {string} [props.className] - An optional CSS class name to apply to the component.
 * @param {IImage} [props.image] - An optional image to display alongside the title and description.
 * @param {II18N} props.i18n - The internationalization settings for the component.
 * @param {boolean} [props.isExternal] - Whether the component should open the link in a new tab or window.
 * @param {string} [props.route] - An optional route to navigate to when the component is clicked.
 * @param {Function} [props.onClickLink] - An optional callback function to execute when the component is clicked.
 * @returns {JSX.Element} A rendered InformationTeaser component.
 */
const InformationTeaser: FC<IInformationTeaser> = ({
  className,
  i18n,
  image,
  isExternal,
  route,
}) => {
  return (
    <section
      aria-label="Portfolio Balance"
      role="contentinfo"
      className={cx(
        "overflow-hidden rounded-lg",
        toolkitCx.layout.background.default,
        toolkitCx.layout.border.default,
        toolkitCx.layout.border.weight.default,
        className
      )}
    >
      {image && <Image {...image} className="h-36 w-full object-cover" />}
      <div className={cx(toolkitCx.layout.spacing.both.md)}>
        <Heading tag="h1" size="2xl" className="mb-2">
          {i18n.title}
        </Heading>
        <Text tag="p">{i18n.copy}</Text>
        {route &&
          (isExternal ? (
            <ExternalLink {...TARGET_BLANK} href={route} className="mt-2" variant="primary">
              {i18n.cta ?? "Learn More"}
            </ExternalLink>
          ) : (
            <Link className="mt-2" to={route} variant="primary">
              {i18n.cta ?? "Learn More"}
            </Link>
          ))}
      </div>
    </section>
  );
};

export default InformationTeaser;
